/* NeedsMeNow — unified "act on this NOW" feed for the doctor dashboard.
 *
 * Combines plan approvals + info-requests + unread lab chat + unread
 * patient chat + at-risk patients into one ranked list. Each item is a
 * single-click jump into the surface where the action happens.
 *
 * Realtime delivery (live updates without manual refresh):
 *   • Primary: Server-Sent Events stream at `/api/doctor/needs-me-now/stream`.
 *     EventSource auto-reconnects on transient drops; the server only
 *     pushes when the snapshot actually changes (delta-push) so we
 *     don't re-render on every poll tick.
 *   • Fallback: REST snapshot at `/api/doctor/needs-me-now`. Used on
 *     initial mount (so the card is never blank) and as a defensive
 *     refetch when the SSE stream errors permanently.
 *
 * Visibility-aware: when the tab is hidden we close the EventSource to
 * save server resources + the doctor's battery. When the tab returns to
 * focus we reopen the stream and immediately fetch a fresh REST snapshot
 * so the "what changed while I was making coffee" delta is correct.
 *
 * New items get a `🆕 NEW` pulse for 8s after they first appear in the
 * feed; the count badge briefly scales when it changes. Both signals
 * are localStorage-free so a hard refresh is a clean slate (intentional
 * — the feed is already filtered to "urgent", so highlighting "what's
 * changed since last poll" is what's meaningful, not "what's new since
 * last login").
 *
 * The card collapses when the list is empty — no one wants a
 * "Nothing here ✅" stuck at the top of their dashboard.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import {
    Sparkle, ChatCircleDots, ChatTeardropDots, ShieldWarning,
    ClipboardText, CaretRight, ArrowRight, Bell, Clock, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const NEW_PULSE_MS = 8_000;        // how long the NEW pulse glows on each item

const KIND_META = {
    plan_approval: { Icon: ClipboardText,  tone: "bg-violet-50 border-violet-200 text-violet-700",  badgeTone: "bg-violet-600 text-white" },
    info_request:  { Icon: Sparkle,        tone: "bg-amber-50 border-amber-200 text-amber-700",     badgeTone: "bg-amber-500 text-white" },
    lab_chat:      { Icon: ChatCircleDots, tone: "bg-blue-50 border-blue-200 text-blue-700",        badgeTone: "bg-blue-600 text-white" },
    patient_chat:  { Icon: ChatTeardropDots, tone: "bg-emerald-50 border-emerald-200 text-emerald-700", badgeTone: "bg-emerald-600 text-white" },
    at_risk:       { Icon: ShieldWarning,  tone: "bg-rose-50 border-rose-200 text-rose-700",        badgeTone: "bg-rose-600 text-white" },
};

const KIND_LABEL = {
    plan_approval: "Plan to approve",
    info_request:  "Planner question",
    lab_chat:      "Lab chat unread",
    patient_chat:  "Patient message",
    at_risk:       "At-risk patient",
};

/* iter100.188 — Tiered snooze model. Only `at_risk` rows can be
   snoozed (industry norm — Epic/Athenahealth: clinical-action items
   like plan-approvals and patient messages CANNOT be silently hidden
   without an audit trail; wellness/advisory alerts CAN be deferred
   with a reason). Preset reason codes mirror the backend allowlist. */
const SNOOZABLE_KINDS = new Set(["at_risk"]);
const SNOOZE_REASON_PRESETS = [
    { code: "already_contacted", label: "Already contacted patient today" },
    { code: "rescheduled",       label: "Rescheduled to next week" },
    { code: "not_concerned",     label: "Not clinically concerned yet" },
    { code: "delegated",         label: "Assigned to my clinic assistant" },
];


export default function NeedsMeNow({ isDay1 = false, onNewCase = null }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    // Item identity is `kind:case_id|patient_id`. We track keys we've
    // already seen so subsequent polls can flag "newly arrived" items
    // with the NEW pulse.
    const seenKeysRef = useRef(new Set());
    const [newKeys, setNewKeys] = useState(new Set());
    const [countBump, setCountBump] = useState(false);
    /* iter100.188 — Snooze dialog state. `snoozeItem` holds the row
       currently being snoozed; null when the dialog is closed. */
    const [snoozeItem, setSnoozeItem] = useState(null);
    const [snoozeSubmitting, setSnoozeSubmitting] = useState(false);

    // Applies a fresh snapshot to local state + computes the "newly
    // arrived" delta against the previous seen-set. Pulled out so the
    // REST seed and the SSE updates share one code path.
    const applySnapshot = (next, isFirstLoad) => {
        const incoming = (next.items || []).map((it) => itemKey(it));
        if (isFirstLoad) {
            seenKeysRef.current = new Set(incoming);
        } else {
            const fresh = incoming.filter((k) => !seenKeysRef.current.has(k));
            if (fresh.length) {
                setNewKeys(new Set(fresh));
                setCountBump(true);
                window.setTimeout(() => setCountBump(false), 700);
                window.setTimeout(() => setNewKeys(new Set()), NEW_PULSE_MS);
            }
            // Update seen set with the full current snapshot — items
            // that fell off are forgotten so they'll pulse if they
            // come back (e.g., a plan was revised + re-submitted).
            seenKeysRef.current = new Set(incoming);
        }
        setData(next);
    };

    // One-shot REST fetch — used to seed the card on mount + on every
    // tab-visible event so the doctor sees the freshest data the
    // moment they refocus.
    const fetchSnapshot = async (isFirstLoad) => {
        try {
            const r = await api.get("/doctor/needs-me-now");
            applySnapshot(r.data, isFirstLoad);
        } catch {
            if (isFirstLoad) setData({ items: [], counts: {} });
        } finally {
            if (isFirstLoad) setLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        let eventSource = null;

        const connectStream = () => {
            if (cancelled || document.hidden) return;
            const token = localStorage.getItem("ca_token");
            if (!token) return;
            // Token-in-query is required because EventSource cannot send
            // a custom Authorization header. Backend validates the JWT
            // the same way as a Bearer header.
            const url = `${API}/doctor/needs-me-now/stream?token=${encodeURIComponent(token)}`;
            try {
                eventSource = new EventSource(url);
            } catch {
                return;  // browser-level failure (extremely rare)
            }
            // Backend emits `event: update` for snapshots + bare `: ping`
            // comments for heartbeats. EventSource filters comments
            // automatically so we only see the named events here.
            eventSource.addEventListener("update", (e) => {
                if (cancelled) return;
                try {
                    const next = JSON.parse(e.data);
                    applySnapshot(next, false);
                } catch {
                    /* malformed payload — ignore, browser will retry */
                }
            });
            // EventSource auto-reconnects on transient errors with its
            // own backoff. We don't need to do anything here; the
            // browser will eventually re-establish + the next `update`
            // event will land. If the connection is _permanently_
            // broken (e.g. token expired) the browser keeps retrying
            // forever — that's why we also keep the visibility-change
            // REST refetch as a defensive backstop.
        };

        const closeStream = () => {
            if (eventSource) {
                eventSource.close();
                eventSource = null;
            }
        };

        // Boot: seed via REST, then open the SSE stream for live updates.
        (async () => {
            await fetchSnapshot(true);
            if (!cancelled) connectStream();
        })();

        // Pause the stream when the tab is backgrounded; on refocus,
        // immediately fetch a snapshot then re-open the stream. This is
        // how the doctor sees "while I was away, 2 plans landed" the
        // moment they tab back in.
        const onVis = () => {
            if (document.hidden) {
                closeStream();
            } else {
                fetchSnapshot(false).then(() => {
                    if (!cancelled && !eventSource) connectStream();
                });
            }
        };
        document.addEventListener("visibilitychange", onVis);

        return () => {
            cancelled = true;
            document.removeEventListener("visibilitychange", onVis);
            closeStream();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* iter102.06 — Hold the skeleton only until BOTH:
       (a) our own /needs-me-now snapshot has resolved (`loading`), AND
       (b) parent has a definitive `isDay1` value (`isDay1 !== null`).
       Parent hydrates `isDay1` synchronously from sessionStorage on
       return-visits (see Dashboard iter102.06), so this branch fires
       for one paint on first-ever visit only — repeat visits skip the
       skeleton entirely. */
    if (loading || isDay1 === null) {
        return (
            <div className="bg-card border border-border rounded-2xl p-6 animate-pulse" data-testid="needs-me-now-loading">
                <div className="h-4 w-40 bg-secondary rounded mb-3" />
                <div className="h-3 w-full bg-secondary rounded mb-2" />
                <div className="h-3 w-3/4 bg-secondary rounded" />
            </div>
        );
    }

    const items = data?.items || [];
    const counts = data?.counts || {};

    // Empty state — quietly celebrate inbox-zero with a slim card, not
    // a full-bleed "nothing here". The doctor needs the screen real
    // estate for the rest of the dashboard.
    // iter100.201 — Day-1 doctor sees a rewritten "your inbox will
    // populate" empty state instead of the misleading "everything is
    // caught up" copy (which implies they've done work they haven't).
    if (items.length === 0) {
        if (isDay1) {
            return (
                <div
                    className="bg-card border border-dashed border-border rounded-2xl p-5 flex items-start gap-3"
                    data-testid="needs-me-now-empty-day1"
                >
                    <div className="w-10 h-10 rounded-full bg-secondary text-muted-foreground grid place-items-center shrink-0">
                        <Sparkle size={18} weight="duotone" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="font-bold text-sm">Your inbox will populate as work comes in</div>
                        <div className="text-xs text-muted-foreground mt-0.5 leading-snug">
                            Cases needing your review, unread lab &amp; patient chats, and CoE escalations all land here.
                        </div>
                        {onNewCase && (
                            <button
                                type="button"
                                onClick={onNewCase}
                                className="mt-2 text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1"
                                data-testid="needs-me-now-empty-day1-cta"
                            >
                                Submit your first case →
                            </button>
                        )}
                    </div>
                </div>
            );
        }
        return (
            <div
                className="bg-gradient-to-br from-primary/10 to-card border border-primary/30 rounded-2xl p-5 flex items-center gap-3"
                data-testid="needs-me-now-empty"
            >
                <div className="w-10 h-10 rounded-full bg-primary/15 text-primary grid place-items-center shrink-0">
                    <Sparkle size={18} weight="fill" />
                </div>
                <div>
                    <div className="font-bold text-sm">Inbox zero — nothing needs you right now</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        Every plan is approved, no unread messages, all patients on track. Nice.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="bg-card border border-border rounded-2xl overflow-hidden"
            data-testid="needs-me-now-card"
        >
            {/* iter100.182 — Rose-accented header so the "Needs me now"
                bar reads as an urgency surface at a glance, matching the
                tone the ComplianceDigestCard uses for drift. Background
                is a soft rose gradient (fades to transparent on the
                right); bell icon + count badge lean into the same hue. */}
            <div className="px-5 py-3.5 border-b border-rose-200/60 bg-gradient-to-r from-rose-50 to-rose-50/40 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Bell size={15} weight="fill" className="text-rose-600" />
                    <h2 className="font-display text-base font-bold">Needs me now</h2>
                    <span
                        className={`text-[10px] tabular-nums font-bold bg-rose-600 text-white px-1.5 py-0.5 rounded transition-transform ${
                            countBump ? "scale-125" : "scale-100"
                        }`}
                        data-testid="needs-me-now-count"
                    >
                        {counts.total ?? items.length}
                    </span>
                </div>
                <div className="hidden sm:flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {counts.plan_approvals + counts.info_requests > 0 && (
                        <CountChip color="text-violet-700 bg-violet-50" n={counts.plan_approvals + counts.info_requests} label="plans" />
                    )}
                    {(counts.lab_chats || 0) > 0 && (
                        <CountChip color="text-blue-700 bg-blue-50" n={counts.lab_chats} label="lab" />
                    )}
                    {(counts.patient_chats || 0) > 0 && (
                        <CountChip color="text-emerald-700 bg-emerald-50" n={counts.patient_chats} label="chat" />
                    )}
                    {(counts.at_risk || 0) > 0 && (
                        <CountChip color="text-rose-700 bg-rose-50" n={counts.at_risk} label="risk" />
                    )}
                </div>
            </div>

            {/* Feed */}
            <ul className="divide-y divide-border" data-testid="needs-me-now-list">
                {items.map((it, i) => {
                    const meta = KIND_META[it.kind] || KIND_META.lab_chat;
                    const Icon = meta.Icon;
                    const isFresh = newKeys.has(itemKey(it));
                    const canSnooze = SNOOZABLE_KINDS.has(it.kind);
                    return (
                        <li
                            key={`${it.kind}-${i}`}
                            data-testid={`needs-me-now-row-${it.kind}-${i}`}
                            className={isFresh ? "animate-pulse bg-emerald-50/40" : ""}
                        >
                            <div className="flex items-stretch group">
                                <Link
                                    to={it.href || "#"}
                                    className="flex-1 min-w-0 px-5 py-3 hover:bg-secondary/30 transition-colors"
                                >
                                    <div className="flex items-start gap-3">
                                        {/* Kind glyph */}
                                        <div className={`w-8 h-8 rounded-full border ${meta.tone} grid place-items-center shrink-0`}>
                                            <Icon size={14} weight="fill" />
                                        </div>
                                        {/* Title + sub */}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="font-semibold text-sm leading-tight">{it.title}</span>
                                                <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${meta.badgeTone}`}>
                                                    {KIND_LABEL[it.kind]}
                                                </span>
                                                {isFresh && (
                                                    <span
                                                        className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-emerald-500 text-white"
                                                        data-testid="needs-me-now-new-pulse"
                                                    >
                                                        🆕 New
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                                                {it.sub}
                                            </div>
                                            {it.rel && (
                                                <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                                                    {it.rel}
                                                </div>
                                            )}
                                        </div>
                                        <CaretRight size={14} className="text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition mt-2 shrink-0" weight="bold" />
                                    </div>
                                </Link>
                                {/* iter100.188 — Snooze button. ONLY renders for
                                    at_risk (advisory) rows; clinical action
                                    items (plans, planner questions, chats)
                                    have no manual dismiss — they must be
                                    acted on for audit-trail integrity. */}
                                {canSnooze && (
                                    <button
                                        type="button"
                                        title="Snooze this alert for 24 hours"
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSnoozeItem(it); }}
                                        className="px-3 border-l border-border text-muted-foreground/60 hover:text-rose-600 hover:bg-rose-50 transition-colors shrink-0 grid place-items-center"
                                        data-testid={`needs-me-now-snooze-btn-${i}`}
                                    >
                                        <Clock size={14} weight="duotone" />
                                    </button>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>

            {/* iter100.188 — Snoozed items ghost strip. Everything the
                doctor has explicitly deferred lives here with an Undo
                affordance, so nothing gets fully lost. Auto-clears when
                the snooze expires (backend filters + we re-fetch on
                the next SSE update). */}
            {(data?.snoozes?.length || 0) > 0 && (
                <SnoozedGhostStrip
                    snoozes={data.snoozes}
                    onUndo={async (item_key) => {
                        try {
                            await api.post("/doctor/needs-me-now/snooze/undo", { item_key });
                            toast.success("Un-snoozed — back in your feed");
                            const r = await api.get("/doctor/needs-me-now");
                            applySnapshot(r.data, false);
                        } catch {
                            toast.error("Couldn't un-snooze. Try again?");
                        }
                    }}
                />
            )}

            {/* Footer — hint that scroll-out items live in their native tabs */}
            <div className="px-5 py-2.5 border-t border-border bg-secondary/20 text-[11px] text-muted-foreground flex items-center justify-between">
                <span>Top {items.length} most urgent. Older items live in their native tabs.</span>
                <Link
                    to="/doctor/cases?status=sent_for_approval"
                    className="font-semibold text-foreground hover:underline inline-flex items-center gap-0.5"
                    data-testid="needs-me-now-see-all-cases"
                >
                    All pending cases <ArrowRight size={10} weight="bold" />
                </Link>
            </div>

            {/* iter100.188 — Snooze dialog. Preset reasons + optional
                free-text (required when reason=other). Mount alongside
                the card so it's available regardless of feed density. */}
            <SnoozeDialog
                item={snoozeItem}
                submitting={snoozeSubmitting}
                onCancel={() => setSnoozeItem(null)}
                onSubmit={async (reason, note) => {
                    setSnoozeSubmitting(true);
                    try {
                        await api.post("/doctor/needs-me-now/snooze", {
                            item_key: itemKey(snoozeItem),
                            reason,
                            note,
                            hours: 24,
                        });
                        toast.success("Snoozed for 24 hours — you'll see it again tomorrow");
                        setSnoozeItem(null);
                        const r = await api.get("/doctor/needs-me-now");
                        applySnapshot(r.data, false);
                    } catch (e) {
                        toast.error(e?.response?.data?.detail || "Couldn't snooze. Try again?");
                    } finally {
                        setSnoozeSubmitting(false);
                    }
                }}
            />
        </div>
    );
}


function CountChip({ n, label, color }) {
    return (
        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded ${color}`}>
            <span className="tabular-nums">{n}</span>
            <span className="opacity-70">{label}</span>
        </span>
    );
}


/* ─── Snoozed items ghost strip (iter100.188) ─────────────────────────
   Sits under the main feed with a subtle muted treatment so it doesn't
   compete visually with active items. Each row shows the deferred item
   + the reason + a countdown until it returns + an Undo link. */
function SnoozedGhostStrip({ snoozes, onUndo }) {
    return (
        <div
            className="px-5 py-3 border-t border-dashed border-border bg-secondary/20"
            data-testid="needs-me-now-snoozed-strip"
        >
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground mb-2 flex items-center gap-1.5">
                <Clock size={11} weight="duotone" /> Snoozed · {snoozes.length}
            </div>
            <ul className="space-y-1.5">
                {snoozes.map((s) => (
                    <li
                        key={s.item_key}
                        className="flex items-center gap-2 text-[11px] text-muted-foreground opacity-80"
                        data-testid={`needs-me-now-snoozed-row-${s.item_key}`}
                    >
                        <span className="font-medium text-foreground/80 truncate">{s.patient_name || "Patient"}</span>
                        <span className="italic truncate">· {s.reason_label}</span>
                        <span className="ml-auto tabular-nums font-mono text-[10px] shrink-0">{fmtCountdown(s.snoozed_until)}</span>
                        <button
                            type="button"
                            onClick={() => onUndo(s.item_key)}
                            className="text-primary font-semibold hover:underline inline-flex items-center gap-0.5 shrink-0"
                            data-testid={`needs-me-now-snoozed-undo-${s.item_key}`}
                        >
                            Undo
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}


function fmtCountdown(untilIso) {
    if (!untilIso) return "";
    try {
        const diff = new Date(untilIso).getTime() - Date.now();
        if (diff <= 0) return "expiring…";
        const hrs = Math.floor(diff / 3_600_000);
        const mins = Math.floor((diff % 3_600_000) / 60_000);
        if (hrs >= 1) return `${hrs}h ${mins}m left`;
        return `${mins}m left`;
    } catch { return ""; }
}


/* ─── Snooze dialog (iter100.188) ─────────────────────────────────────
   Preset reason chips + optional "other → free-text" fallback. Enforces
   min-10-char note when reason=other (matches backend guard). */
function SnoozeDialog({ item, onCancel, onSubmit, submitting }) {
    const [reason, setReason] = useState("already_contacted");
    const [note, setNote] = useState("");
    const [isOther, setIsOther] = useState(false);

    useEffect(() => {
        if (item) {
            setReason("already_contacted");
            setNote("");
            setIsOther(false);
        }
    }, [item]);

    const canSubmit = !submitting && (isOther ? note.trim().length >= 10 : true);

    return (
        <Dialog open={!!item} onOpenChange={(o) => { if (!o) onCancel(); }}>
            <DialogContent data-testid="snooze-dialog" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Clock size={16} weight="duotone" className="text-rose-600" />
                        Snooze this alert for 24 hours
                    </DialogTitle>
                    <DialogDescription>
                        {item?.title || "This item"} will disappear from your feed until tomorrow. A reason is required so your practice audit log stays complete.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-1">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Why are you snoozing this?
                    </div>
                    <div className="grid grid-cols-1 gap-1.5">
                        {SNOOZE_REASON_PRESETS.map((p) => (
                            <button
                                key={p.code}
                                type="button"
                                onClick={() => { setReason(p.code); setIsOther(false); }}
                                className={`text-left text-sm px-3 py-2 rounded-lg border transition-colors ${
                                    !isOther && reason === p.code
                                        ? "border-rose-400 bg-rose-50 text-rose-900 font-semibold"
                                        : "border-border hover:border-rose-200 hover:bg-rose-50/40"
                                }`}
                                data-testid={`snooze-reason-${p.code}`}
                            >
                                {p.label}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => { setIsOther(true); setReason("other"); }}
                            className={`text-left text-sm px-3 py-2 rounded-lg border transition-colors ${
                                isOther
                                    ? "border-rose-400 bg-rose-50 text-rose-900 font-semibold"
                                    : "border-border hover:border-rose-200 hover:bg-rose-50/40"
                            }`}
                            data-testid="snooze-reason-other"
                        >
                            Other reason (write it in)
                        </button>
                    </div>

                    {isOther && (
                        <div className="space-y-1">
                            <Textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="e.g. Patient's mom called clinic yesterday, we're seeing them Monday…"
                                rows={3}
                                className="text-sm resize-none"
                                data-testid="snooze-note-input"
                            />
                            <div className={`text-[10px] ${note.trim().length >= 10 ? "text-muted-foreground" : "text-rose-600"}`}>
                                {note.trim().length}/10 characters minimum
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="flex gap-2 sm:justify-end">
                    <Button variant="outline" onClick={onCancel} disabled={submitting} data-testid="snooze-cancel-btn">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => onSubmit(reason, isOther ? note.trim() : null)}
                        disabled={!canSubmit}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        data-testid="snooze-confirm-btn"
                    >
                        {submitting ? "Snoozing…" : "Snooze 24 hours"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/** Stable identity for an inbox row so we can detect "newly arrived"
 *  items across polls. */
function itemKey(it) {
    return `${it.kind}:${it.case_id || it.patient_id || ""}`;
}
